"use client"

import React from "react"
import { toast } from "sonner"
import {
  Facebook, X, Instagram, Linkedin,
  Phone, Youtube, Mail, MapPin, User as UserIcon
} from "lucide-react"
import { TelegramIcon, WhatsappIcon } from "react-share"

import { Tiktok } from "@/app/components/atoms/TiktokIcon"
import { HeadingText } from "@/components/atoms/HeadingText/heading-text"
import { Tag } from "@/components/atoms/Tag"
import Rating from "../Rating/rating"
import UserInfo from "../UserInfo"
import SocialLinks from "../SocialLinks"
import { ContactDetailItem } from "../ContactDetailItem"

interface CandidateProfileProps {
  name: string
  roles: string
  imageUrl: string
  skills: string[]
  socialLinks: { platform: string; url: string }[]
  averageRating: number
  phone: string
  email: string
  location: { city?: string; state?: string }
  business?: string
  translations: Record<string, string>
  candidateId?: string
  isReviewer?: boolean
}

const CandidateProfile = ({
  business,
  name,
  roles,
  imageUrl,
  skills,
  socialLinks,
  averageRating,
  phone,
  email,
  location,
  translations,
  candidateId,
  isReviewer = false,
}: CandidateProfileProps) => {
  const formattedSocialLinks = socialLinks.map((link) => ({
    href: link.url,
    label: link.platform,
    icon:
      link.platform === "facebook" ? <Facebook size={22} /> :
        link.platform === "twitter" ? <X size={22} /> :
          link.platform === "linkedin" ? <Linkedin size={22} /> :
            link.platform === "instagram" ? <Instagram size={22} /> :
              link.platform === "youtube" ? <Youtube size={22} /> :
                link.platform === "tiktok" ? <Tiktok /> :
                  link.platform === "whatsapp" ? <WhatsappIcon size={22} /> :
                    link.platform === "telegram" ? <TelegramIcon size={22} /> : null
  }))

  const handleContactMe = () => {
    if (phone) {
      window.location.href = `tel:${phone}`
    } else {
      toast.error("Phone number not available.")
    }
  }

  return (
    <div className="mx-auto w-full max-w-xs rounded-2xl border bg-white shadow-md dark:bg-gray-800">
      <div className="flex flex-col items-center p-6">
        <UserInfo
          name={business && business.length > 0 ? business : name}
          roles={roles}
          imageUrl={imageUrl}
          orientation="vertical"
        />

        <div className="flex items-center gap-2">
          <Rating stars={averageRating} />
          <span className="text-sm text-gray-600 dark:text-gray-400">{averageRating}</span>
        </div>

        <div className="mt-4">
          <SocialLinks links={formattedSocialLinks} spacing="small" buttonSize="icon" />
        </div>

        <button
          onClick={handleContactMe}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 transition"
        >
          <Phone size={18} />
          <span>{translations.contact}</span>
        </button>
      </div>

      {skills.length > 0 && (
        <div className="border-t px-6 py-4">
          <HeadingText level={5}>{translations.skills}</HeadingText>
          <div className="mt-2 flex flex-wrap gap-2">
            {skills.map((skill) => (
              <Tag
                key={skill}
                label={skill}
                bgColor="bg-blue-100"
                textColor="text-blue-600"
              />
            ))}
          </div>
        </div>
      )}

      <div className="border-t px-6 py-4">
        <HeadingText level={5}>{translations.contactDetails}</HeadingText>
        <div className="mt-3 space-y-3">
          <ContactDetailItem
            icon={<UserIcon className="h-6 w-6 text-muted-foreground" />}
            label={translations.employer}
            value={name}
          />
          <ContactDetailItem
            icon={<Phone className="h-6 w-6 text-muted-foreground" />}
            label={translations.phone}
            value={phone}
          />
          <ContactDetailItem
            icon={<Mail className="h-6 w-6 text-muted-foreground" />}
            label={translations.email}
            value={email}
          />
          {(location?.city || location?.state) && (
            <ContactDetailItem
              icon={<MapPin className="h-6 w-6 text-muted-foreground" />}
              label={translations.location}
              value={
                location.city && location.state
                  ? `${location.city}, ${location.state}`
                  : location.city || location.state
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CandidateProfile
